import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BoltIcon } from "../components/Bolts";
import { ArrowLeft, Plus, Trash2, Save, Upload, Eye, EyeOff, Loader2, Pencil, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dollars = (c) => (c / 100).toFixed(2);
const toCents = (v) => Math.round(parseFloat(v || "0") * 100) || 0;

const StickyWicketPage = () => {
    const [tab, setTab] = useState("packs");
    const [config, setConfig] = useState(null);
    const [packs, setPacks] = useState([]);
    const [decals, setDecals] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [c, p, d] = await Promise.all([
                axios.get(`${API}/sticker/config`),
                axios.get(`${API}/sticker/packs`, { params: { include_hidden: true } }),
                axios.get(`${API}/sticker/decals`, { params: { include_hidden: true } }),
            ]);
            setConfig(c.data);
            setPacks(p.data || []);
            setDecals(d.data || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    };

    useEffect(() => { loadAll(); }, []);

    if (loading || !config) {
        return <div className="min-h-[60vh] flex items-center justify-center text-storm-mist">Loading The Sticky Wicket…</div>;
    }

    return (
        <div data-testid="sticky-wicket-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-12 md:py-16">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-5 hover:gap-3 transition-all">
                        <ArrowLeft className="w-4 h-4" /> Admin
                    </Link>
                    <p className="eyebrow flex items-center gap-2 mb-4"><BoltIcon className="w-3.5 h-3.5" /> Stickers & Decals</p>
                    <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">The <span className="text-storm-bolt">Sticky Wicket</span></h1>
                    <p className="text-storm-mist mt-3 max-w-xl">Quick uploader for sticker packs and vinyl decals — no Photo Studio needed.</p>

                    <div className="flex gap-0 mt-8 border border-white/10 w-fit">
                        {[["packs", "Sticker Packs"], ["decals", "Vinyl Decals"], ["settings", "Settings"]].map(([k, label]) => (
                            <button key={k} onClick={() => setTab(k)} data-testid={`sw-tab-${k}`}
                                className={`font-heading uppercase tracking-wider text-sm px-5 py-3 ${tab === k ? "bg-storm-bolt text-black" : "text-white/70 hover:text-white"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-x py-10 md:py-14">
                {tab === "packs" && <PacksTab config={config} packs={packs} reload={loadAll} />}
                {tab === "decals" && <DecalsTab config={config} decals={decals} reload={loadAll} />}
                {tab === "settings" && <SettingsTab config={config} setConfig={setConfig} reload={loadAll} />}
            </section>
        </div>
    );
};

/* ================ SETTINGS ================ */
const SettingsTab = ({ config, setConfig, reload }) => {
    const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(config)));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/sticker/config`, draft);
            await reload();
            setConfig(draft);
            alert("Saved.");
        } catch (e) {
            alert("Couldn't save. Try again.");
        } finally { setSaving(false); }
    };

    const updSizeList = (key, i, field, val) => {
        const copy = { ...draft };
        copy[key] = [...copy[key]];
        copy[key][i] = { ...copy[key][i], [field]: field.includes("price_cents") ? toCents(val) : val };
        setDraft(copy);
    };
    const addSizeRow = (key, extra) => setDraft({ ...draft, [key]: [...(draft[key] || []), { size: "", regular_price_cents: 0, uv_price_cents: 0, ...extra }] });
    const rmRow = (key, i) => setDraft({ ...draft, [key]: draft[key].filter((_, idx) => idx !== i) });

    const vis = draft.section_visibility || {};
    const toggleVis = (k) => setDraft({ ...draft, section_visibility: { ...vis, [k]: !vis[k] } });

    return (
        <div className="max-w-3xl space-y-10">
            {/* Section visibility */}
            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-1">Section Visibility</h3>
                <p className="text-storm-mist text-xs mb-3">Toggle which sections customers can see on the storefront.</p>
                <div className="grid sm:grid-cols-2 gap-2">
                    {[
                        ["regular_blind_bags", "Regular Blind Bags"],
                        ["uv_blind_bags", "UV Resistant Blind Bags"],
                        ["regular_packs", "Regular Theme Packs"],
                        ["uv_packs", "UV Resistant Theme Packs"],
                    ].map(([k, label]) => (
                        <button key={k} onClick={() => toggleVis(k)}
                            className={`flex items-center gap-3 px-4 py-3 border text-sm text-left ${vis[k] ? "border-storm-bolt/50 text-white" : "border-white/10 text-storm-mist/50"}`}>
                            <span className={`w-3 h-3 rounded-full ${vis[k] ? "bg-storm-bolt" : "bg-white/10"}`} />
                            {label} — {vis[k] ? "Visible" : "Hidden"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Pack sizes with dual pricing */}
            <DualPriceList title="Theme Pack Sizes & Prices" hint="Prices for packs where the customer picks their theme."
                rows={draft.pack_sizes || []} onChange={(i, f, v) => updSizeList("pack_sizes", i, f, v)}
                onAdd={() => addSizeRow("pack_sizes")} onRemove={(i) => rmRow("pack_sizes", i)} testid="cfg-packsize" />

            <DualPriceList title="Blind Bag Sizes & Prices" hint="Prices for mystery blind bags."
                rows={draft.blind_bag_sizes || []} onChange={(i, f, v) => updSizeList("blind_bag_sizes", i, f, v)}
                onAdd={() => addSizeRow("blind_bag_sizes")} onRemove={(i) => rmRow("blind_bag_sizes", i)} testid="cfg-blindsize" />

            {/* Sticker size options */}
            <WordList title="Sticker Size Options" hint='Approximate sticker sizes (e.g. 3", 5"). Actual stickers will be within ±1" of the listed size.'
                items={draft.sticker_size_options || []} onChange={(items) => setDraft({ ...draft, sticker_size_options: items })} testid="cfg-stickersize" />

            <PriceList title="Decal Sizes & Prices" hint="Bigger = more. The customer's size choice sets the decal price."
                rows={draft.decal_sizes || []} onChange={(i, f, v) => updSizeList("decal_sizes", i, f, v)}
                onAdd={() => addSizeRow("decal_sizes", { price_cents: 0 })} onRemove={(i) => rmRow("decal_sizes", i)} testid="cfg-decalsize" />

            <WordList title="Decal Colors" hint="The color choices a customer can pick for any decal."
                items={draft.decal_colors || []} onChange={(items) => setDraft({ ...draft, decal_colors: items })} testid="cfg-color" />

            <WordList title="Decal Categories" hint="Groupings shown as tiles on the Vinyl Decals page."
                items={draft.decal_categories || []} onChange={(items) => setDraft({ ...draft, decal_categories: items })} testid="cfg-decalcat" />

            <button onClick={save} disabled={saving} data-testid="sw-save-config" className="btn-primary disabled:opacity-50">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Save all settings</>}
            </button>
        </div>
    );
};

/* Dual-price row: size | regular $ | UV $ */
const DualPriceList = ({ title, hint, rows, onChange, onAdd, onRemove, testid }) => (
    <div>
        <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-1">{title}</h3>
        <p className="text-storm-mist text-xs mb-3">{hint}</p>
        <div className="space-y-2">
            {rows.length > 0 && (
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-storm-mist/60 px-1">
                    <span className="flex-1">Size</span>
                    <span className="w-28 text-center">Regular $</span>
                    <span className="w-28 text-center">UV $</span>
                    <span className="w-9" />
                </div>
            )}
            {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                    <input value={r.size} onChange={(e) => onChange(i, "size", e.target.value)} placeholder="e.g. 10-Pack"
                        data-testid={`${testid}-label-${i}`}
                        className="flex-1 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                    <div className="flex items-center gap-1">
                        <span className="text-storm-mist">$</span>
                        <input value={dollars(r.regular_price_cents || 0)} onChange={(e) => onChange(i, "regular_price_cents", e.target.value)} inputMode="decimal"
                            data-testid={`${testid}-reg-${i}`}
                            className="w-24 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-storm-mist">$</span>
                        <input value={dollars(r.uv_price_cents || 0)} onChange={(e) => onChange(i, "uv_price_cents", e.target.value)} inputMode="decimal"
                            data-testid={`${testid}-uv-${i}`}
                            className="w-24 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                    </div>
                    <button onClick={() => onRemove(i)} className="text-storm-mist hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
            ))}
        </div>
        <button onClick={onAdd} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-storm-bolt mt-3 hover:gap-2 transition-all">
            <Plus className="w-4 h-4" /> Add row
        </button>
    </div>
);

/* Single-price row (decal sizes) */
const PriceList = ({ title, hint, rows, onChange, onAdd, onRemove, testid }) => (
    <div>
        <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-1">{title}</h3>
        <p className="text-storm-mist text-xs mb-3">{hint}</p>
        <div className="space-y-2">
            {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                    <input value={r.size} onChange={(e) => onChange(i, "size", e.target.value)} placeholder="Label"
                        data-testid={`${testid}-label-${i}`}
                        className="flex-1 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                    <div className="flex items-center gap-1">
                        <span className="text-storm-mist">$</span>
                        <input value={dollars(r.price_cents)} onChange={(e) => onChange(i, "price_cents", e.target.value)} inputMode="decimal"
                            data-testid={`${testid}-price-${i}`}
                            className="w-24 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                    </div>
                    <button onClick={() => onRemove(i)} className="text-storm-mist hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
            ))}
        </div>
        <button onClick={onAdd} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-storm-bolt mt-3 hover:gap-2 transition-all">
            <Plus className="w-4 h-4" /> Add row
        </button>
    </div>
);

const WordList = ({ title, hint, items, onChange, testid }) => {
    const [val, setVal] = useState("");
    return (
        <div>
            <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-1">{title}</h3>
            <p className="text-storm-mist text-xs mb-3">{hint}</p>
            <div className="flex flex-wrap gap-2 mb-3">
                {items.map((it, i) => (
                    <span key={i} className="inline-flex items-center gap-2 bg-storm-navy border border-white/10 px-3 py-1.5 text-sm text-white">
                        {it}
                        <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-storm-mist hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                ))}
                {items.length === 0 && <span className="text-storm-mist text-sm">None yet.</span>}
            </div>
            <div className="flex gap-2">
                <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add one…"
                    data-testid={`${testid}-input`}
                    onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onChange([...items, val.trim()]); setVal(""); } }}
                    className="bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt min-w-[200px]" />
                <button onClick={() => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }}
                    className="btn-primary !text-xs !px-3 !py-2">Add</button>
            </div>
        </div>
    );
};

/* ================ PACKS ================ */
const PacksTab = ({ config, packs, reload }) => {
    const [name, setName] = useState("");
    const [stickerType, setStickerType] = useState("regular");
    const [size, setSize] = useState("");
    const [stickerSize, setStickerSize] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!name.trim() || !size || !file) { alert("Fill in name, size, and pick a photo."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            fd.append("sticker_type", stickerType);
            fd.append("size", size);
            fd.append("sticker_size", stickerSize);
            fd.append("image", file);
            await axios.post(`${API}/sticker/packs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setName(""); setStickerType("regular"); setSize(""); setStickerSize(""); setFile(null);
            await reload();
        } catch (e) {
            alert("Couldn't add that pack. Try again.");
        } finally { setBusy(false); }
    };

    const toggleHide = async (p) => {
        await axios.post(`${API}/sticker/packs/${p.id}/hide`, null, { params: { hidden: !p.hidden } });
        reload();
    };

    const typeLabel = (t) => t === "uv_resistant" ? "UV" : "Reg";
    const sizeOpts = config.sticker_size_options || [];

    return (
        <div className="grid lg:grid-cols-2 gap-10">
            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Add a Sticker Pack</h3>
                <div className="space-y-4 max-w-md">
                    <Field label="Pack name">
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="pack-name"
                            className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt" />
                    </Field>

                    <Field label="Sticker type">
                        <div className="flex gap-0 border border-white/10 w-fit">
                            {[["regular", "Regular"], ["uv_resistant", "UV Resistant"]].map(([val, label]) => (
                                <button key={val} onClick={() => setStickerType(val)}
                                    className={`text-sm px-4 py-2.5 font-heading uppercase tracking-wider ${stickerType === val ? "bg-storm-bolt text-black" : "text-white/70 hover:text-white"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Pack size">
                            <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="pack-size"
                                className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt">
                                <option value="">Choose…</option>
                                {(config.pack_sizes || []).map((ps) => {
                                    const p = stickerType === "uv_resistant" ? ps.uv_price_cents : ps.regular_price_cents;
                                    return <option key={ps.size} value={ps.size}>{ps.size} (${dollars(p || 0)})</option>;
                                })}
                            </select>
                        </Field>
                        <Field label="Sticker size (approx)">
                            <select value={stickerSize} onChange={(e) => setStickerSize(e.target.value)}
                                className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt">
                                <option value="">Optional…</option>
                                {sizeOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>

                    <Field label="Pack photo">
                        <label className="flex items-center gap-2 border border-dashed border-white/20 px-3 py-3 cursor-pointer text-storm-mist hover:border-storm-bolt hover:text-storm-bolt text-sm">
                            <Upload className="w-4 h-4" /> {file ? file.name : "Choose a photo"}
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </label>
                    </Field>

                    <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50">
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding</> : <><Plus className="w-4 h-4" /> Add pack</>}
                    </button>
                </div>
            </div>

            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Your Packs ({packs.length})</h3>
                <div className="space-y-3">
                    {packs.length === 0 && <p className="text-storm-mist text-sm">No packs yet.</p>}
                    {packs.map((p) => (
                        <div key={p.id} className={`surface-solid p-3 flex items-center gap-3 ${p.hidden ? "opacity-50" : ""}`}>
                            <img src={`${API}/catalog/image/${p.image_id}`} alt="" className="w-14 h-14 object-cover border border-white/10" />
                            <div className="flex-1 min-w-0">
                                <div className="text-white text-sm truncate">{p.name}</div>
                                <div className="text-storm-mist text-xs">
                                    {p.size} · {typeLabel(p.sticker_type)} · ${dollars(p.price_cents)}
                                    {p.sticker_size && ` · ~${p.sticker_size}`}
                                </div>
                            </div>
                            <button onClick={() => toggleHide(p)} className="text-storm-mist hover:text-storm-bolt p-2" title={p.hidden ? "Unhide" : "Hide"}>
                                {p.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ================ DECALS ================ */
const DecalsTab = ({ config, decals, reload }) => {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editCat, setEditCat] = useState("");

    const categories = config.decal_categories || [];

    const submit = async () => {
        if (!name.trim() || !category || !file) { alert("Add a name, pick a category, and pick a photo."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            fd.append("category", category);
            fd.append("image", file);
            await axios.post(`${API}/sticker/decals`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setName(""); setCategory(""); setFile(null);
            await reload();
        } catch (e) {
            alert("Couldn't add that decal. Try again.");
        } finally { setBusy(false); }
    };

    const toggleHide = async (d) => {
        await axios.post(`${API}/sticker/decals/${d.id}/hide`, null, { params: { hidden: !d.hidden } });
        reload();
    };

    const deleteDecal = async (d) => {
        if (!window.confirm(`Delete "${d.name}" permanently?`)) return;
        try {
            await axios.delete(`${API}/sticker/decals/${d.id}`);
            await reload();
        } catch (e) { alert("Couldn't delete. Try again."); }
    };

    const saveCategory = async (d) => {
        try {
            await axios.patch(`${API}/sticker/decals/${d.id}`, null, { params: { category: editCat } });
            setEditingId(null);
            await reload();
        } catch (e) { alert("Couldn't update. Try again."); }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-10">
            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Add a Vinyl Decal</h3>
                <p className="text-storm-mist text-xs mb-4 max-w-md">Color and size choices come from the Settings tab.</p>
                <div className="space-y-4 max-w-md">
                    <Field label="Decal name">
                        <input value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt" />
                    </Field>
                    <Field label="Category">
                        <select value={category} onChange={(e) => setCategory(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt">
                            <option value="">Choose…</option>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {categories.length === 0 && <p className="text-red-400 text-xs mt-1.5">Add categories in the Settings tab first.</p>}
                    </Field>
                    <Field label="Decal photo">
                        <label className="flex items-center gap-2 border border-dashed border-white/20 px-3 py-3 cursor-pointer text-storm-mist hover:border-storm-bolt hover:text-storm-bolt text-sm">
                            <Upload className="w-4 h-4" /> {file ? file.name : "Choose a photo"}
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </label>
                    </Field>
                    <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50">
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding</> : <><Plus className="w-4 h-4" /> Add decal</>}
                    </button>
                </div>
            </div>

            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Your Decals ({decals.length})</h3>
                <div className="space-y-3">
                    {decals.length === 0 && <p className="text-storm-mist text-sm">No decals yet.</p>}
                    {decals.map((d) => (
                        <div key={d.id} className={`surface-solid p-3 flex items-center gap-3 ${d.hidden ? "opacity-50" : ""}`}>
                            <img src={`${API}/catalog/image/${d.image_id}`} alt="" className="w-14 h-14 object-cover border border-white/10" />
                            <div className="flex-1 min-w-0">
                                <div className="text-white text-sm truncate">{d.name}</div>
                                {editingId === d.id ? (
                                    <div className="flex items-center gap-1 mt-1">
                                        <select value={editCat} onChange={(e) => setEditCat(e.target.value)}
                                            className="bg-storm-void border border-white/10 text-white text-xs px-2 py-1 focus:outline-none focus:border-storm-bolt">
                                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <button onClick={() => saveCategory(d)} className="text-storm-bolt hover:text-white p-1"><Check className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => setEditingId(null)} className="text-storm-mist hover:text-red-400 p-1"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ) : (
                                    <div className="text-storm-mist text-xs flex items-center gap-1">
                                        {d.category || "Uncategorized"}
                                        <button onClick={() => { setEditingId(d.id); setEditCat(d.category || categories[0] || ""); }}
                                            className="text-storm-mist/50 hover:text-storm-bolt"><Pencil className="w-3 h-3" /></button>
                                    </div>
                                )}
                            </div>
                            <button onClick={() => toggleHide(d)} className="text-storm-mist hover:text-storm-bolt p-2" title={d.hidden ? "Unhide" : "Hide"}>
                                {d.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                            <button onClick={() => deleteDecal(d)} className="text-storm-mist hover:text-red-400 p-2" title="Delete permanently">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">{label}</label>
        {children}
    </div>
);

export default StickyWicketPage;
